import { fromThreadMessageLike } from "../../runtime/utils/thread-message-like";
import { appendToolInteraction } from "../../runtime/utils/tool-interactions";
import { generateId } from "../../utils/id";
import type {
  ChatModelAdapter,
  ChatModelRunResult,
} from "../../runtime/utils/chat-model-adapter";
import { shouldContinue } from "./should-continue";
import { getAutoStatus } from "../../runtime/utils/auto-status";
import {
  type ExportedMessageRepository,
  withoutOrphanedMessages,
} from "../../runtime/utils/message-repository";
import type { LocalRuntimeOptionsBase } from "./local-runtime-options";
import { consumeSuggestionResult } from "../../adapters/suggestion";
import type {
  AddToolResultOptions,
  ResumeToolCallOptions,
  RespondToToolApprovalOptions,
  ThreadSuggestion,
  ThreadRuntimeCore,
  Unstable_RecordToolInteractionOptions,
  StartRunConfig,
  ResumeRunConfig,
} from "../../runtime/interfaces/thread-runtime-core";
import { BaseThreadRuntimeCore } from "../../runtime/base/base-thread-runtime-core";
import type {
  AppendMessage,
  ThreadAssistantMessage,
  ThreadAssistantMessagePart,
  ToolCallMessagePart,
} from "../../types/message";
import type { RunConfig, ThreadMessage } from "../../types/message";
import { MessageNotSentError, toAssistantError } from "../../types/error";
import type { ModelContextProvider } from "../../model-context/types";
import {
  createMessageQueue,
  type MessageQueueController,
} from "../../runtime/queue/message-queue";
import type { QueuePlacement } from "../../runtime/queue/external-thread-queue-adapter";
import {
  EMPTY_QUEUE_ITEMS,
  type QueueItemState,
} from "../../runtime/queue/queue-item";
import {
  captureThreadRuntimeGeneration,
  invalidateThreadRuntime,
} from "../../runtime/utils/thread-runtime-lifecycle";

class AbortError extends Error {
  override name = "AbortError";
  detach: boolean;

  constructor(detach: boolean, message?: string) {
    super(message);
    this.detach = detach;
  }
}

// `shouldContinue` resumes only on `tool-calls` and `resumeToolCall` throws, so
// an imported `interrupt` with no interrupt payload would be stranded here.
// Provenance cannot gate it: a repository that went through JSON has no marker.
// The replacement stays content-derived, so a message with nothing resultless
// left to act on lands on `complete` rather than an unactionable pause.
const withLocalPauseReason = (message: ThreadMessage): ThreadMessage => {
  if (
    message.role !== "assistant" ||
    message.status.type !== "requires-action" ||
    message.status.reason !== "interrupt" ||
    message.content.some(
      (c) =>
        c.type === "tool-call" && c.result === undefined && c.interrupt != null,
    )
  )
    return message;
  return {
    ...message,
    status: getAutoStatus(
      false,
      false,
      false,
      message.content.some(
        (c) => c.type === "tool-call" && c.result === undefined,
      ),
    ),
  };
};

// A run resumes by restarting from its message, which drops every turn after
// it, so a message that a later turn follows can never resume: its open
// approvals are cancelled and its run settles as cancelled.
const withCancelledPause = (
  message: ThreadAssistantMessage,
): ThreadAssistantMessage => {
  let cancelled = false;
  const content = message.content.map((part): ThreadAssistantMessagePart => {
    if (
      part.type !== "tool-call" ||
      part.approval === undefined ||
      part.approval.approved !== undefined ||
      part.approval.resolution !== undefined
    )
      return part;
    cancelled = true;
    return { ...part, approval: { ...part.approval, resolution: "cancelled" } };
  });
  const open =
    message.status.type === "running" ||
    message.status.type === "requires-action";
  if (!cancelled && !open) return message;
  return {
    ...message,
    content,
    status: open ? { type: "incomplete", reason: "cancelled" } : message.status,
  };
};

const withLocalPauseReasons = (
  data: ExportedMessageRepository,
): ExportedMessageRepository => {
  const followed = new Set(data.messages.map((item) => item.parentId));
  return {
    ...data,
    messages: data.messages.map((item) => ({
      ...item,
      message:
        item.message.role === "assistant" && followed.has(item.message.id)
          ? withCancelledPause(item.message)
          : withLocalPauseReason(item.message),
    })),
  };
};

const withoutToolInteractions = (message: ThreadMessage): ThreadMessage => {
  if (message.role !== "assistant") return message;
  let hasInteractions = false;
  const content = message.content.map((part): ThreadAssistantMessagePart => {
    if (part.type !== "tool-call") return part;
    const nestedMessages = part.messages?.map(withoutToolInteractions);
    const hasNestedInteractions = nestedMessages?.some(
      (nestedMessage, index) => nestedMessage !== part.messages?.[index],
    );
    if (
      part.unstable_interactions === undefined &&
      hasNestedInteractions !== true
    ) {
      return part;
    }
    hasInteractions = true;
    const { unstable_interactions: _, ...withoutInteractions } = part;
    return hasNestedInteractions
      ? { ...withoutInteractions, messages: nestedMessages! }
      : withoutInteractions;
  });
  return hasInteractions ? { ...message, content } : message;
};

export class LocalThreadRuntimeCore
  extends BaseThreadRuntimeCore
  implements ThreadRuntimeCore
{
  public readonly capabilities = {
    switchToBranch: true,
    switchBranchDuringRun: true,
    edit: true,
    delete: false,
    reload: true,
    refetchThread: false,
    cancel: true,
    unstable_copy: true,
    speech: false,
    dictation: false,
    voice: false,
    attachments: false,
    feedback: false,
    queue: false,
    answerToolCall: true,
  };

  private abortController: AbortController | null = null;

  private _queue: MessageQueueController | null = null;
  // Identifies the dispatch in flight, not merely that one is: consecutive
  // queue runs overlap, and the previous dispatch settles after the next one
  // has already started.
  private _queueRunInFlight: object | null = null;
  private _activeRun: { cancelled: boolean } | null = null;
  private _runGeneration = 0;
  // A metadata change such as feedback, and a tool result on a running message, replace a message without superseding the run that is streaming it; any other replacement ends that run, whose later chunks would overwrite it.
  private _messageReplacements = new WeakMap<
    ThreadAssistantMessage,
    { message: ThreadAssistantMessage; toolCallId?: string }
  >();

  private _historyWrites = new Map<string, Promise<void>>();

  // Writes for one message id must land in issue order; an earlier paused
  // snapshot arriving after the terminal write would resurrect the pause.
  private _chainHistoryWrite(
    id: string,
    write: () => Promise<void>,
  ): Promise<void> {
    // The first write for an id is issued synchronously, so it reaches the adapter before a turn appended under that message in the same tick.
    const pending = this._historyWrites.get(id);
    let next: Promise<void>;
    if (pending) next = pending.then(write, write);
    else {
      try {
        next = Promise.resolve(write());
      } catch (error) {
        next = Promise.reject(error);
      }
    }
    const stored = next.then(
      () => {},
      () => {},
    );
    this._historyWrites.set(id, stored);
    void stored.then(() => {
      if (this._historyWrites.get(id) === stored) {
        this._historyWrites.delete(id);
      }
    });
    return next;
  }

  // A result or decision recorded after a run wrote its message rewrites the stored entry from the repository's current state, so a rewrite queued after a subscriber's change still carries it; a message the history never received is appended, and a running message is written by its own run once it settles.
  private _persistMessageUpdate(messageId: string) {
    const history = this._options.adapters.history;
    if (!history) return;
    let entry: { parentId: string | null; message: ThreadMessage };
    try {
      entry = this.repository.getMessage(messageId);
    } catch {
      return;
    }
    const { parentId, message } = entry;
    if (message.role !== "assistant" || message.status.type === "running")
      return;
    if (
      this._unwrittenMessages.has(message.id) &&
      !history.update &&
      message.status.type === "requires-action"
    )
      return;
    this._persistSettled(parentId, message)?.catch(() => {});
  }

  // A message whose roundtrip is in flight belongs to that run, even after it
  // yields a pause, so the run settles it when the roundtrip ends. `import`
  // replaces every message both refer to, so it clears them.
  private _roundtripsInFlight = new Map<
    string,
    { controller: AbortController; resumedFromPause: boolean }
  >();
  private _followedDuringRun = new Set<string>();

  // Messages a run created that the history has not received; a message loaded from history is never in this set, so its writes are updates.
  private _unwrittenMessages = new Set<string>();

  private _persistSettled(
    parentId: string | null,
    message: ThreadAssistantMessage,
  ) {
    const history = this._options.adapters.history;
    if (!history) return;
    const write = this._unwrittenMessages.delete(message.id)
      ? history.append.bind(history)
      : history.update?.bind(history);
    if (!write) return;
    const item = { parentId, message, runConfig: this._lastRunConfig };
    return this._chainHistoryWrite(message.id, () => write(item));
  }

  private _cancelPause(messageId: string | null) {
    if (messageId === null) return;
    let entry: { parentId: string | null; message: ThreadMessage };
    try {
      entry = this.repository.getMessage(messageId);
    } catch {
      return;
    }
    if (entry.message.role !== "assistant") return;
    if (this._roundtripsInFlight.has(messageId)) {
      this._followedDuringRun.add(messageId);
      const snapshot = withCancelledPause(entry.message);
      const history = this._options.adapters.history;
      // A followed run skips its final write, so a resumed pause a history without `update` already holds is appended again here with what the run added.
      if (
        history &&
        !history.update &&
        this._roundtripsInFlight.get(messageId)?.resumedFromPause &&
        !this._unwrittenMessages.has(messageId)
      ) {
        const item = {
          parentId: entry.parentId,
          message: snapshot,
          runConfig: this._lastRunConfig,
        };
        return this._chainHistoryWrite(messageId, () => history.append(item));
      }
      return this._persistSettled(entry.parentId, snapshot);
    }
    const message = withCancelledPause(entry.message);
    if (message === entry.message) return;
    this.repository.addOrUpdateMessage(entry.parentId, message);
    return this._persistSettled(entry.parentId, message);
  }

  // A message that a later turn follows never resumes, since a roundtrip
  // restarts from it and would drop that turn. A follow-up recorded while its
  // run was open ended the pause too, even once that turn is deleted. The
  // resume starts from the stored entry, which a subscriber may have replaced
  // while it was notified.
  private _resumeIfReady(messageId: string) {
    const stored = this.getMessageById(messageId);
    if (
      stored?.message.role !== "assistant" ||
      this.repository.hasChildren(messageId) ||
      this._followedDuringRun.has(messageId) ||
      !shouldContinue(stored.message, this._options.unstable_humanToolNames)
    )
      return false;
    this._runLoop(stored.parentId, stored.message, this._lastRunConfig).catch(
      () => {},
    );
    return true;
  }

  public readonly isDisabled = false;
  public readonly isSendDisabled = false;

  private _isLoading = false;
  public get isLoading() {
    return this._isLoading;
  }

  private _suggestions: readonly ThreadSuggestion[] = [];
  private _suggestionsController: AbortController | null = null;
  public get suggestions(): readonly ThreadSuggestion[] {
    return this._suggestions;
  }

  public get adapters() {
    return this._options.adapters;
  }

  constructor(
    contextProvider: ModelContextProvider,
    options: LocalRuntimeOptionsBase,
  ) {
    super(contextProvider);
    this.__internal_setOptions(options);
  }

  private _options!: LocalRuntimeOptionsBase;

  private _lastRunConfig: RunConfig = {};

  private _getThreadId?: () => string | undefined;

  public __internal_setGetThreadId(getThreadId: () => string | undefined) {
    this._getThreadId = getThreadId;
  }

  private _getInitializePromise?: () => Promise<unknown> | undefined;

  public __internal_setGetInitializePromise(
    getPromise: () => Promise<unknown> | undefined,
  ) {
    this._getInitializePromise = getPromise;
  }

  public get extras() {
    return undefined;
  }

  public __internal_setOptions(options: LocalRuntimeOptionsBase) {
    if (this._options === options) return;

    const previousHistory = this._options?.adapters.history;
    this._options = options;
    if (!options.adapters.voice && this.voice) {
      try {
        this.disconnectVoice();
      } catch (error) {
        console.error(
          "[assistant-ui] Voice cleanup threw after the adapter changed",
          error,
        );
      }
    }

    let hasUpdates = false;

    if (!options.adapters.suggestion) {
      this._suggestionsController?.abort();
      this._suggestionsController = null;
      if (this._suggestions.length > 0) {
        this._suggestions = [];
        hasUpdates = true;
      }
    }

    const canSpeak = options.adapters?.speech !== undefined;
    if (this.capabilities.speech !== canSpeak) {
      this.capabilities.speech = canSpeak;
      hasUpdates = true;
    }

    const canDictate = options.adapters?.dictation !== undefined;
    if (this.capabilities.dictation !== canDictate) {
      this.capabilities.dictation = canDictate;
      hasUpdates = true;
    }

    const canVoice = options.adapters?.voice !== undefined;
    if (this.capabilities.voice !== canVoice) {
      this.capabilities.voice = canVoice;
      hasUpdates = true;
    }

    const canAttach = options.adapters?.attachments !== undefined;
    if (this.capabilities.attachments !== canAttach) {
      this.capabilities.attachments = canAttach;
      hasUpdates = true;
    }

    const canFeedback = options.adapters?.feedback !== undefined;
    if (this.capabilities.feedback !== canFeedback) {
      this.capabilities.feedback = canFeedback;
      hasUpdates = true;
    }

    const canDelete = options.adapters?.history?.delete !== undefined;
    if (this.capabilities.delete !== canDelete) {
      this.capabilities.delete = canDelete;
      hasUpdates = true;
    }

    const canQueue = options.unstable_enableMessageQueue === true;
    if (canQueue && !this._queue) {
      this._queue = createMessageQueue({
        run: (message) => {
          // release the queue when the dispatch settles, even if it rejects
          // before reaching startRun's finally, so a failure can't deadlock it
          const dispatch = {};
          this._queueRunInFlight = dispatch;
          const generation = this._runGeneration;
          // the tail may have moved since the message was enqueued
          void this._runAppend({
            ...message,
            parentId: this._resolveAppendParent(
              this.messages.at(-1)?.id ?? null,
            ),
          })
            .finally(() => {
              if (this._queueRunInFlight === dispatch) {
                this._queueRunInFlight = null;
                // A dispatch that failed before starting a run settles here;
                // runs that did start release from _runLoop.
                if (this._runGeneration === generation)
                  this._queue?.notifyIdle();
              }
            })
            .catch(() => {});
        },
      });
      if (this.voice) this._queue.hold();
      this._queue.subscribe(() => this._notifySubscribers());
    } else if (!canQueue && this._queue) {
      this._queue = null;
    }
    if (this.capabilities.queue !== canQueue) {
      this.capabilities.queue = canQueue;
      hasUpdates = true;
    }

    if (hasUpdates) this._notifySubscribers();

    if (
      this._loadRequested &&
      !this._loadPromise &&
      !previousHistory &&
      options.adapters.history &&
      this.messages.length === 0
    ) {
      void this.__internal_load().catch((error: unknown) => {
        console.error(
          "[assistant-ui] local thread history load failed:",
          error,
        );
      });
    }
  }

  private _loadPromise: Promise<void> | undefined;
  private _loadRequested = false;
  public __internal_load() {
    this._loadRequested = true;
    if (this._loadPromise) return this._loadPromise;
    if (!this.adapters.history) return Promise.resolve();

    const promise = this.adapters.history.load();

    this._isLoading = true;

    this._loadPromise = promise
      .then((repo) => {
        if (!repo) return;
        const { repository, droppedIds } = withoutOrphanedMessages(repo);
        if (droppedIds.length > 0) {
          console.warn(
            "[assistant-ui] Skipped history messages with missing parents:",
            droppedIds,
          );
        }
        if (
          repo.headId != null &&
          !repo.messages.some((item) => item.message.id === repo.headId)
        ) {
          console.warn(
            "[assistant-ui] History head is not among the loaded messages:",
            repo.headId,
          );
        }
        this.repository.import(withLocalPauseReasons(repository));
        if (repository.messages.length > 0) {
          this.ensureInitialized();
        }
        this._notifySubscribers();

        const resume = this.adapters.history?.resume?.bind(
          this.adapters.history,
        );
        if (repo.unstable_resume && resume) {
          this.startRun(
            {
              parentId: this.repository.headId,
              sourceId: this.repository.headId,
              runConfig: this._lastRunConfig,
            },
            resume,
          ).catch(() => {});
        }
      })
      .finally(() => {
        this._isLoading = false;
        this._notifySubscribers();
      });

    // Notified after the promise is stored so a subscriber that appends
    // re-entrantly finds the barrier it has to wait on.
    this._notifySubscribers();

    return this._loadPromise;
  }

  // The import that ends a load replaces the repository contents and resets
  // the head, so a message added while it is in flight would be left on a
  // discarded branch. Awaiting the load promise keeps the wait bounded by the
  // adapter call, unlike polling `isLoading` for a notification that a
  // superseded runtime never sends.
  private _getHistoryLoadBarrier(): Promise<void> | undefined {
    if (!this._isLoading || !this._loadPromise) return undefined;
    return this._loadPromise.catch(() => {});
  }

  public async append(message: AppendMessage): Promise<void> {
    message = {
      ...message,
      parentId: this._resolveAppendParent(message.parentId),
    };
    if (this.voice) return this._appendToVoiceSession(message);
    if (this._isVoiceMessage(message.sourceId))
      throw new Error("Voice transcript messages cannot be edited");
    const isTail = message.parentId === (this.messages.at(-1)?.id ?? null);
    const willRun = message.startRun ?? message.role === "user";
    if (this._queue && willRun && isTail) {
      if (message.steer ?? this._queueRunInFlight !== null)
        this._queue.adapter.steer(message);
      else this._queue.adapter.enqueue(message);
      return;
    }
    if (
      this._queue &&
      !isTail &&
      (this._options.unstable_queueClearOnRewind ?? true)
    )
      this._queue.clear();
    return this._runAppend(message);
  }

  protected override _commitVoiceMessage(
    message: ThreadMessage,
  ): void | Promise<void> {
    const generation = captureThreadRuntimeGeneration(this);
    const commit = (notify: boolean) => {
      if (generation.aborted) {
        this._dropVoiceMessage(message.id, notify);
        return;
      }
      const parentId = this.repository.headId;
      this.repository.addOrUpdateMessage(parentId, message);
      const settledWrite = this._cancelPause(parentId);
      this.repository.resetHead(message.id);
      const history = this._options.adapters.history;
      const historyWrite = history
        ? this._chainHistoryWrite(message.id, () =>
            history.append({ parentId, message }),
          )
        : undefined;
      void historyWrite?.catch(() => {});
      // The write lands whether or not the session still carries the message,
      // so the notification cannot ride on removing it: hanging up mid load
      // clears the side list before the barrier resolves.
      this._dropVoiceMessage(message.id, false);
      if (notify) this._notifySubscribers();
      return settledWrite
        ? Promise.all([settledWrite, historyWrite]).then(() => {})
        : historyWrite;
    };
    const barrier = this._getVoiceCommitBarrier();
    return barrier ? barrier.then(() => commit(true)) : commit(false);
  }

  protected override _onVoiceConnected(): void {
    this._queue?.hold();
  }

  protected override _onVoiceDisconnected(): void {
    this._queue?.release();
  }

  public getQueueItems(): readonly QueueItemState[] {
    // Reads can arrive during base-thread construction, before the queue field
    // is assigned, so guard against the unset field.
    return this._queue?.adapter.items ?? EMPTY_QUEUE_ITEMS;
  }

  public getSteerQueueItems(): readonly QueueItemState[] {
    return this._queue?.adapter.steerItems ?? EMPTY_QUEUE_ITEMS;
  }

  public moveQueueItem(queueItemId: string, placement: QueuePlacement): void {
    this._queue?.adapter.move(queueItemId, placement);
  }

  public removeQueueItem(queueItemId: string): void {
    this._queue?.adapter.remove(queueItemId);
  }

  private _rollbackAppend(messageId: string) {
    try {
      this.repository.deleteMessage(messageId);
    } catch {
      return;
    }
    this._notifySubscribers();
  }

  private _pendingAppends = 0;

  protected override _isRunActive(): boolean {
    return this._pendingAppends > 0 || super._isRunActive();
  }

  private async _runAppend(rawMessage: AppendMessage): Promise<void> {
    this._pendingAppends += 1;
    try {
      await this._runAppendInner(rawMessage);
    } finally {
      this._pendingAppends -= 1;
    }
  }

  private async _runAppendInner(rawMessage: AppendMessage): Promise<void> {
    // Stamped here rather than in `append` so a queued message is gated after
    // the flush re-pointed its parentId at the current tail.
    const generation = captureThreadRuntimeGeneration(this);

    const loadBarrier = this._getHistoryLoadBarrier();
    if (loadBarrier) {
      const wasAtTail =
        rawMessage.parentId === (this.messages.at(-1)?.id ?? null);
      await loadBarrier;
      if (generation.aborted) return;
      // A message aimed at the tail follows the tail the load established; one
      // aimed at a specific parent keeps it, the way an edit does.
      if (wasAtTail) {
        rawMessage = {
          ...rawMessage,
          parentId: this._resolveAppendParent(this.messages.at(-1)?.id ?? null),
        };
      }
    }

    const message = this.enrichAppendMetadata(rawMessage);
    this.ensureInitialized();

    const newMessage = fromThreadMessageLike(message, generateId(), {
      type: "complete",
      reason: "unknown",
    });
    this.repository.addOrUpdateMessage(message.parentId, newMessage);
    this._notifySubscribers();

    // Initialization only gates the history write and the run; the message
    // is already on screen. A failed barrier rolls the optimistic message
    // back and rejects as an unsent message so the composer restores the
    // draft; a thread invalidated mid-wait rolls back silently.
    try {
      const initPromise = this._getInitializePromise?.();
      if (initPromise) {
        await initPromise;
      }
    } catch (error) {
      this._rollbackAppend(newMessage.id);
      if (generation.aborted) return;
      if (message.parentId !== null) this._resumeIfReady(message.parentId);
      const notSent = new MessageNotSentError();
      notSent.cause = error;
      throw notSent;
    }
    if (generation.aborted) {
      this._rollbackAppend(newMessage.id);
      return;
    }
    const settledWrite = this._cancelPause(message.parentId);
    const history = this._options.adapters.history;
    const messageWrite = history
      ? this._chainHistoryWrite(newMessage.id, () =>
          history.append({
            parentId: message.parentId,
            message: newMessage,
            ...(message.runConfig !== undefined && {
              runConfig: message.runConfig,
            }),
          }),
        )
      : undefined;
    const historyWrite = settledWrite
      ? Promise.all([settledWrite, messageWrite]).then(() => {})
      : messageWrite;
    void historyWrite?.catch(() => {});

    const startRun = message.startRun ?? message.role === "user";
    if (startRun) {
      const [runResult, historyResult] = await Promise.allSettled([
        this.startRun({
          parentId: newMessage.id,
          sourceId: message.sourceId,
          runConfig: message.runConfig ?? {},
        }),
        historyWrite,
      ]);
      if (runResult.status === "rejected") throw runResult.reason;
      if (historyResult.status === "rejected") throw historyResult.reason;
    } else {
      this.repository.switchToBranch(newMessage.id);
      this._notifySubscribers();
      await historyWrite;
    }
  }

  public async deleteMessage(messageId: string): Promise<void> {
    const adapter = this._options.adapters.history;
    if (!adapter?.delete)
      throw new Error("Runtime does not support deleting messages.");

    const messages = this.repository.getMessages();
    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) throw new Error("Message not found.");

    const message = messages[messageIndex]!;
    const parentId = messages[messageIndex - 1]?.id ?? null;
    const items = [{ parentId, message }];

    await adapter.delete(items);

    this.repository.deleteMessage(messageId);
    this._notifySubscribers();
  }

  public resumeRun({ stream, ...startConfig }: ResumeRunConfig): Promise<void> {
    if (!stream)
      throw new Error("You must pass a stream parameter to resume runs.");
    return this.startRun(startConfig, stream);
  }

  public exportExternalState(): any {
    throw new Error("Runtime does not support exporting external states.");
  }

  public override import(data: ExportedMessageRepository) {
    this._roundtripsInFlight.clear();
    this._followedDuringRun.clear();
    this._unwrittenMessages.clear();
    super.import(withLocalPauseReasons(data));
  }

  public importExternalState(): void {
    throw new Error("Runtime does not support importing external states.");
  }

  public unstable_notifySessionReset(): void {
    throw new Error("Runtime does not support resetting sessions.");
  }

  public async startRun(
    { parentId, sourceId, runConfig }: StartRunConfig,
    runCallback?: ChatModelAdapter["run"],
  ): Promise<void> {
    this.ensureInitialized();
    if (this.voice)
      throw new Error("Cannot start a run while a voice session is connected");
    if (this._isVoiceMessage(sourceId))
      throw new Error("Voice transcript messages cannot be reloaded");

    const settledWrite = this._cancelPause(parentId);

    // add assistant message
    const id = generateId();
    const message: ThreadAssistantMessage = {
      id,
      role: "assistant",
      status: { type: "running" },
      content: [],
      metadata: {
        unstable_state: this.state,
        unstable_annotations: [],
        unstable_data: [],
        steps: [],
        custom: {},
      },
      createdAt: new Date(),
    };
    this._unwrittenMessages.add(id);

    const run = this._runLoop(parentId, message, runConfig, runCallback);
    if (!settledWrite) return run;
    const [runResult, settledResult] = await Promise.allSettled([
      run,
      settledWrite,
    ]);
    if (runResult.status === "rejected") throw runResult.reason;
    if (settledResult.status === "rejected") throw settledResult.reason;
  }

  private async _runLoop(
    parentId: string | null,
    message: ThreadAssistantMessage,
    runConfig: RunConfig | undefined,
    runCallback?: ChatModelAdapter["run"],
  ): Promise<void> {
    if (this.voice)
      throw new Error("Cannot start a run while a voice session is connected");
    this._notifyEventSubscribers("runStart", {});

    const run = {
      cancelled: false,
      resumedFromPause: message.status.type === "requires-action",
    };
    this._activeRun = run;
    this._runGeneration++;

    let active = false;
    try {
      // mark busy for runs not started through the queue (regenerate, resume)
      this._queue?.notifyBusy();
      this._suggestions = [];
      this._suggestionsController?.abort();
      this._suggestionsController = null;
      this._notifySubscribers();

      do {
        message = await this.performRoundtrip(
          parentId,
          message,
          runConfig,
          run,
          runCallback,
        );
        runCallback = undefined;
        if (this._activeRun !== run) break;
        let replacement = this._messageReplacements.get(message);
        while (replacement) {
          message = replacement.message;
          replacement = this._messageReplacements.get(message);
        }
        if (this.getMessageById(message.id)?.message !== message) break;
      } while (
        shouldContinue(message, this._options.unstable_humanToolNames) &&
        !this.repository.hasChildren(message.id)
      );
    } finally {
      this._notifyEventSubscribers("runEnd", {});
      // the settle belongs to this run only while it is still the active run
      // or was cancelled (the engine expects a cancelled run's settle); a run
      // superseded by a newer one stays silent
      active = this._activeRun === run;
      if (active) this._activeRun = null;
      if (active || run.cancelled) {
        queueMicrotask(() => this._queue?.notifyIdle());
      }
    }

    if (
      active &&
      this.adapters.suggestion &&
      message.status?.type !== "requires-action"
    ) {
      this._suggestionsController = new AbortController();
      const signal = this._suggestionsController.signal;
      const adapter = this.adapters.suggestion;
      void (async () => {
        try {
          const promiseOrGenerator = adapter.generate({
            messages: this.messages,
            signal,
          });
          await consumeSuggestionResult(promiseOrGenerator, {
            signal,
            onUpdate: (r) => {
              this._suggestions = r;
              this._notifySubscribers();
            },
          });
        } catch {}
      })();
    }
  }

  private async performRoundtrip(
    parentId: string | null,
    message: ThreadAssistantMessage,
    runConfig: RunConfig | undefined,
    run: { cancelled: boolean; resumedFromPause: boolean },
    runCallback?: ChatModelAdapter["run"],
  ) {
    const messages = parentId ? this.repository.getMessages(parentId) : [];
    // A message here that is running or whose roundtrip is still in flight
    // belongs to the run this roundtrip aborts.
    const modelMessages = messages.map((m) =>
      withoutToolInteractions(
        m.role === "assistant" &&
          (m.status.type === "running" || this._roundtripsInFlight.has(m.id))
          ? withCancelledPause(m)
          : m,
      ),
    );

    // abort existing run
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;

    const initialContent = message.content;
    const initialAnnotations = message.metadata?.unstable_annotations;
    const initialData = message.metadata?.unstable_data;
    const initialSteps = message.metadata?.steps;
    const initialCustom = message.metadata?.custom;
    const externalToolCallIds = new Set<string>();
    let hasStoredMessage = true;
    try {
      this.repository.getMessage(message.id);
    } catch {
      hasStoredMessage = false;
    }
    const syncOwnedMessage = () => {
      if (!hasStoredMessage) return this._activeRun === run;
      try {
        let ownedMessage = message;
        let replacement = this._messageReplacements.get(ownedMessage);
        while (replacement) {
          if (replacement.toolCallId !== undefined) {
            externalToolCallIds.add(replacement.toolCallId);
          }
          ownedMessage = replacement.message;
          replacement = this._messageReplacements.get(ownedMessage);
        }
        if (this.repository.getMessage(message.id).message !== ownedMessage)
          return false;
        message = ownedMessage;
        return true;
      } catch {
        return false;
      }
    };
    const withExternalResults = (parts: ThreadAssistantMessage["content"]) => {
      const interactions = new Map<
        string,
        ToolCallMessagePart["unstable_interactions"]
      >();
      for (const part of message.content) {
        if (
          part.type === "tool-call" &&
          part.unstable_interactions !== undefined
        ) {
          interactions.set(part.toolCallId, part.unstable_interactions);
        }
      }
      const withInteractions = parts.map((part) => {
        if (
          part.type !== "tool-call" ||
          part.unstable_interactions !== undefined
        ) {
          return part;
        }
        const unstable_interactions = interactions.get(part.toolCallId);
        return unstable_interactions === undefined
          ? part
          : { ...part, unstable_interactions };
      });

      if (externalToolCallIds.size === 0) return withInteractions;
      const previousToolCalls = new Map<string, ToolCallMessagePart[]>();
      for (const part of message.content) {
        if (
          part.type !== "tool-call" ||
          !externalToolCallIds.has(part.toolCallId)
        )
          continue;
        const occurrences = previousToolCalls.get(part.toolCallId) ?? [];
        occurrences.push(part);
        previousToolCalls.set(part.toolCallId, occurrences);
      }
      const incomingOccurrences = new Map<string, number>();
      return withInteractions.map((part) => {
        if (part.type !== "tool-call") return part;
        const occurrence = incomingOccurrences.get(part.toolCallId) ?? 0;
        incomingOccurrences.set(part.toolCallId, occurrence + 1);
        if (part.result !== undefined && part.isPreliminary !== true)
          return part;
        const completed = previousToolCalls.get(part.toolCallId)?.[occurrence];
        if (
          !completed ||
          completed.result === undefined ||
          completed.isPreliminary === true
        )
          return part;
        const {
          isPreliminary: _,
          artifact: _artifact,
          modelContent: _modelContent,
          ...settledPart
        } = part;
        return {
          ...settledPart,
          result: completed.result,
          isError: completed.isError,
          ...(completed.artifact !== undefined && {
            artifact: completed.artifact,
          }),
          ...(completed.modelContent !== undefined && {
            modelContent: completed.modelContent,
          }),
        };
      });
    };
    const updateMessage = (m: Partial<ChatModelRunResult>) => {
      if (!syncOwnedMessage()) return;
      const newSteps = m.metadata?.steps;
      const steps = newSteps
        ? [...(initialSteps ?? []), ...newSteps]
        : undefined;

      const newAnnotations = m.metadata?.unstable_annotations;
      const newData = m.metadata?.unstable_data;
      const annotations = newAnnotations
        ? [...(initialAnnotations ?? []), ...newAnnotations]
        : undefined;
      const data = newData ? [...(initialData ?? []), ...newData] : undefined;

      const content = m.content
        ? withExternalResults([...initialContent, ...m.content])
        : undefined;

      message = {
        ...message,
        ...(content ? { content } : undefined),
        status: m.status ?? message.status,
        ...(m.metadata
          ? {
              metadata: {
                ...message.metadata,
                ...(m.metadata.unstable_state !== undefined
                  ? { unstable_state: m.metadata.unstable_state }
                  : undefined),
                ...(annotations
                  ? { unstable_annotations: annotations }
                  : undefined),
                ...(data ? { unstable_data: data } : undefined),
                ...(steps ? { steps } : undefined),
                ...(m.metadata?.timing
                  ? { timing: m.metadata.timing }
                  : undefined),
                ...(m.metadata?.custom
                  ? {
                      custom: {
                        ...(initialCustom ?? {}),
                        ...m.metadata.custom,
                      },
                    }
                  : undefined),
              },
            }
          : undefined),
      };
      this.repository.addOrUpdateMessage(parentId, message);
      hasStoredMessage = true;
      this._notifySubscribers();
    };

    const maxSteps = this._options.maxSteps ?? 2;

    this._roundtripsInFlight.set(message.id, {
      controller: abortController,
      resumedFromPause: run.resumedFromPause,
    });
    try {
      const steps = message.metadata?.steps?.length ?? 0;
      if (steps >= maxSteps) {
        updateMessage({
          status: {
            type: "incomplete",
            reason: "tool-calls",
          },
        });
        return message;
      }

      updateMessage({
        status: {
          type: "running",
        },
      });

      // Switch to the new message branch right after adding it for the first time
      this.repository.switchToBranch(message.id);
      this._notifySubscribers();

      this._lastRunConfig = runConfig ?? {};
      // unstable_composerMetadata is composer-only (stamped onto the outgoing
      // message); never expose it to the chat-model adapter's run context.
      const { unstable_composerMetadata: _, ...context } =
        this.getModelContext();

      runCallback =
        runCallback ??
        this.adapters.chatModel.run.bind(this.adapters.chatModel);

      const abortSignal = abortController.signal;
      const shouldCancelMessage = () =>
        abortSignal.aborted &&
        (message.status.type === "running" ||
          (message.status.type === "requires-action" &&
            (this._activeRun !== run ||
              shouldContinue(message, this._options.unstable_humanToolNames))));
      const threadId = this._getThreadId?.();
      const promiseOrGenerator = runCallback({
        messages: modelMessages,
        runConfig: this._lastRunConfig,
        abortSignal,
        context,
        unstable_assistantMessageId: message.id,
        unstable_threadId: threadId,
        unstable_parentId: parentId,
        unstable_getMessage() {
          syncOwnedMessage();
          return withoutToolInteractions(message);
        },
      });

      // handle async iterator for streaming results
      if (Symbol.asyncIterator in promiseOrGenerator) {
        for await (const r of promiseOrGenerator) {
          if (abortSignal.aborted) {
            if (shouldCancelMessage()) {
              updateMessage({
                status: { type: "incomplete", reason: "cancelled" },
              });
            }
            break;
          }

          updateMessage(r);
        }
      } else {
        updateMessage(await promiseOrGenerator);
      }

      if (shouldCancelMessage()) {
        updateMessage({
          status: { type: "incomplete", reason: "cancelled" },
        });
      } else if (message.status.type === "running") {
        updateMessage({
          status: { type: "complete", reason: "unknown" },
        });
      }
    } catch (e) {
      if (e instanceof AbortError) {
        updateMessage({
          status: { type: "incomplete", reason: "cancelled" },
        });
      } else if (e instanceof Error && e.name === "AbortError") {
        updateMessage({
          status: { type: "incomplete", reason: "cancelled" },
        });
      } else {
        updateMessage({
          status: {
            type: "incomplete",
            reason: "error",
            error: toAssistantError(e),
          },
        });

        throw e;
      }
    } finally {
      if (this.abortController === abortController) {
        this.abortController = null;
      }
      // A roundtrip replaced by a resume of the same message leaves it, and any
      // follow-up recorded meanwhile, to the roundtrip that replaced it.
      const holdsMessage =
        this._roundtripsInFlight.get(message.id)?.controller ===
        abortController;
      if (holdsMessage) this._roundtripsInFlight.delete(message.id);

      const history = this._options.adapters.history;
      const ownsCurrentMessage = syncOwnedMessage();
      let settled = false;
      let written: Promise<void> | undefined;
      const followedDuringRun =
        holdsMessage && this._followedDuringRun.delete(message.id);
      if (followedDuringRun) {
        const stored = this.getMessageById(message.id);
        if (stored?.message.role === "assistant") {
          const cancelled = withCancelledPause(stored.message);
          if (cancelled !== stored.message) {
            this.repository.addOrUpdateMessage(stored.parentId, cancelled);
            settled = true;
            if (ownsCurrentMessage) message = cancelled;
            else written = this._persistSettled(stored.parentId, cancelled);
          }
        }
      }
      if (holdsMessage && !ownsCurrentMessage && !written) {
        const stored = this.getMessageById(message.id);
        if (
          stored?.message.role === "assistant" &&
          (stored.message.status.type === "complete" ||
            stored.message.status.type === "incomplete") &&
          this._unwrittenMessages.has(message.id)
        ) {
          written = this._persistSettled(stored.parentId, stored.message);
        }
      }
      const isTerminal =
        message.status.type === "complete" ||
        message.status.type === "incomplete";
      const isPausing =
        message.status.type === "requires-action" &&
        !shouldContinue(message, this._options.unstable_humanToolNames);

      // Pauses are written only for adapters that can rewrite the entry later;
      // an append-only adapter would strand a half-finished run in history.
      if (
        ownsCurrentMessage &&
        (isTerminal || (isPausing && history?.update))
      ) {
        if (
          isTerminal &&
          run.resumedFromPause &&
          !followedDuringRun &&
          !this._unwrittenMessages.has(message.id) &&
          history &&
          !history.update
        ) {
          const item = { parentId, message, runConfig: this._lastRunConfig };
          written = this._chainHistoryWrite(message.id, () =>
            history.append(item),
          );
        } else {
          written = this._persistSettled(parentId, message);
        }
      }
      // Notified after the write is queued, so a change a subscriber makes in
      // response is written after it.
      if (settled) this._notifySubscribers();
      if (written) await written;
    }
    return message;
  }

  public detach() {
    invalidateThreadRuntime(this);
    // drop the queue so pending items cannot dispatch on a detached thread
    this._queue = null;
    const error = new AbortError(true);
    this.abortController?.abort(error);
    this.abortController = null;
    this._suggestionsController?.abort();
    this._suggestionsController = null;
  }

  public cancelRun() {
    if (this._queue) {
      if (this._options.unstable_queueClearOnCancel ?? true) {
        this._queue.clear();
      } else {
        this._queue.notifyCancelled();
        if (this._activeRun) this._activeRun.cancelled = true;
      }
    }
    const error = new AbortError(false);
    this.abortController?.abort(error);
    this.abortController = null;
    this._suggestionsController?.abort();
    this._suggestionsController = null;
  }

  protected override _onMessageMetadataChanged(
    previousMessage: ThreadAssistantMessage,
    message: ThreadAssistantMessage,
  ): void {
    this._messageReplacements.set(previousMessage, { message });
    this._persistMessageUpdate(message.id);
  }

  public addToolResult({
    messageId,
    toolCallId,
    result,
    isError,
    artifact,
    modelContent,
  }: AddToolResultOptions) {
    if (this.voice)
      throw new Error(
        "Cannot add a tool result while a voice session is connected",
      );
    const messageData = this.repository.getMessage(messageId);
    const { parentId } = messageData;
    let { message } = messageData;

    if (message.role !== "assistant")
      throw new Error("Tried to add tool result to non-assistant message");

    let added = false;
    let found = false;
    const newContent = message.content.map((c) => {
      if (c.type !== "tool-call") return c;
      if (c.toolCallId !== toolCallId) return c;
      found = true;
      if (c.result === undefined || c.isPreliminary === true) added = true;
      const { isPreliminary: _isPreliminary, ...part } = c;
      // artifact and modelContent are optional; only override when supplied so
      // a later result that omits them does not clobber a stored value.
      return {
        ...part,
        result,
        isError,
        ...(artifact !== undefined && { artifact }),
        ...(modelContent !== undefined && { modelContent }),
      };
    });

    if (!found)
      throw new Error("Tried to add tool result to non-existing tool call");

    const previousMessage = message;
    message = {
      ...message,
      content: newContent,
    };
    if (previousMessage.status.type === "running") {
      this._messageReplacements.set(previousMessage, {
        message,
        toolCallId,
      });
    }
    this.repository.addOrUpdateMessage(parentId, message);
    this._notifySubscribers();
    if (!added) return;

    // a result may arrive mid-run or on a non-head message; the resume
    // intentionally aborts any in-flight run, unlike respondToToolApproval
    if (!this._resumeIfReady(messageId)) this._persistMessageUpdate(messageId);
  }

  public resumeToolCall(_options: ResumeToolCallOptions) {
    throw new Error(
      "Local runtime does not support resuming tool calls. For human-in-the-loop tools, list the tool in unstable_humanToolNames and complete the call with addToolResult.",
    );
  }

  public async unstable_recordToolInteraction({
    messageId,
    toolCallId,
    interaction,
  }: Unstable_RecordToolInteractionOptions): Promise<void> {
    let messageData: { parentId: string | null; message: ThreadMessage };
    try {
      messageData = this.repository.getMessage(messageId);
    } catch {
      throw new Error(
        "Tried to record a tool interaction on a non-existing message",
      );
    }
    const { parentId, message: previousMessage } = messageData;
    if (previousMessage.role !== "assistant") {
      throw new Error(
        "Tried to record a tool interaction on a non-assistant message",
      );
    }
    const toolCallIndex = previousMessage.content.findIndex(
      (part) => part.type === "tool-call" && part.toolCallId === toolCallId,
    );
    if (toolCallIndex === -1) {
      throw new Error(
        "Tried to record a tool interaction on a non-existing tool call",
      );
    }
    const target = previousMessage.content[toolCallIndex]!;
    if (target.type !== "tool-call") {
      throw new Error(
        "Tried to record a tool interaction on a non-existing tool call",
      );
    }
    const message: ThreadAssistantMessage = {
      ...previousMessage,
      content: previousMessage.content.map((part, index) =>
        index === toolCallIndex
          ? {
              ...part,
              unstable_interactions: appendToolInteraction(
                target.unstable_interactions,
                interaction,
              ),
            }
          : part,
      ),
    };
    if (previousMessage.status.type === "running") {
      this._messageReplacements.set(previousMessage, { message });
    }
    this.repository.addOrUpdateMessage(parentId, message);
    this._notifySubscribers();
    this._persistMessageUpdate(message.id);
  }

  public respondToToolApproval({
    approvalId,
    approved,
    optionId,
    text,
    reason,
  }: RespondToToolApprovalOptions): Promise<void> {
    if (this.voice)
      throw new Error(
        "Cannot respond to a tool approval while a voice session is connected",
      );
    let message = this.repository
      .getMessages()
      .findLast(
        (m): m is ThreadAssistantMessage =>
          m.role === "assistant" &&
          m.content.some(
            (c) => c.type === "tool-call" && c.approval?.id === approvalId,
          ),
      );

    if (!message)
      throw new Error("Tried to respond to a non-existing tool approval");

    if (this.abortController !== null)
      throw new Error(
        "Tried to respond to a tool approval while a run is in progress",
      );

    if (message.status?.type !== "requires-action")
      throw new Error(
        "Tried to respond to a tool approval on a message whose status is not requires-action",
      );

    if (this.repository.hasChildren(message.id))
      throw new Error(
        "Tried to respond to a tool approval on a message that later messages follow",
      );
    if (this._followedDuringRun.has(message.id))
      throw new Error(
        "Tried to respond to a tool approval that was cancelled or expired",
      );

    const target = message.content.find(
      (c) => c.type === "tool-call" && c.approval?.id === approvalId,
    );
    if (target?.type !== "tool-call" || !target.approval)
      throw new Error("Tried to respond to a non-existing tool approval");
    if (target.approval.resolution !== undefined)
      throw new Error(
        "Tried to respond to a tool approval that was cancelled or expired",
      );
    if (target.approval.approved !== undefined)
      throw new Error("Tried to respond to an already decided tool approval");

    const targetApproval = target.approval;
    const newContent = message.content.map((c) => {
      if (c !== target) return c;
      const approval = {
        ...targetApproval,
        approved,
        ...(optionId != null && { optionId }),
        ...(text != null && { text }),
        ...(reason != null && { reason }),
      };
      if (approved) return { ...c, approval };
      return {
        ...c,
        approval,
        result: { error: reason || "Tool approval denied" },
        isError: true,
      };
    });

    message = { ...message, content: newContent };
    const { parentId } = this.repository.getMessage(message.id);
    this.repository.addOrUpdateMessage(parentId, message);
    this._notifySubscribers();
    this._notifyToolApprovalAnswered(
      message.id,
      target.toolCallId,
      target.toolName,
      approved,
    );

    const stored = this.getMessageById(message.id);
    if (
      this.repository.headId === message.id &&
      stored?.message.role === "assistant" &&
      shouldContinue(stored.message, this._options.unstable_humanToolNames)
    ) {
      this._runLoop(stored.parentId, stored.message, this._lastRunConfig).catch(
        () => {},
      );
    } else {
      this._persistMessageUpdate(message.id);
    }

    return Promise.resolve();
  }
}
